import { useState, useRef, useContext, useEffect } from 'react';
import styles from './UploadScreen.module.css';
import searchStyles from '../../../components/ListingScore/ListingScore.module.css';
import AsinNotFoundModal from './AsinNotFoundModal';
import { useAmazonStatus } from '../../../hooks/useAmazonStatus';
import AuthContext from '../../../context/AuthContext';
import { useAmazonSync } from '../../../context/AmazonSyncContext';
import step1Image from '../../../assets/dashboard_assets/step1_excel_preview.png';
import step4Image from '../../../assets/dashboard_assets/step4_upload_preview.png';
import { FaPlus, FaTimes, FaExclamationCircle } from 'react-icons/fa'; // Icons
import { BsStars } from 'react-icons/bs'; // Sparkle icon
import excelIcon from '../../../assets/dashboard_assets/excel_icon.png';
import zipIcon from '../../../assets/dashboard_assets/zip_icon.png';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import {
    findColumnValue,
    SKU_COLUMNS, BRAND_COLUMNS, CATEGORY_COLUMNS, SUB_CATEGORY_COLUMNS,
    PRODUCT_TYPE_COLUMNS, TITLE_COLUMNS, COLOR_COLUMNS, MATERIAL_COLUMNS,
    SIZE_COLUMNS, EAN_COLUMNS, KEYWORDS_COLUMNS, DESCRIPTION_COLUMNS,
    BANNED_KEYWORDS_COLUMNS, BULLET_1_COLUMNS, BULLET_2_COLUMNS,
    BULLET_3_COLUMNS, BULLET_4_COLUMNS, BULLET_5_COLUMNS,
    CORE_DETAIL_1_COLUMNS, CORE_DETAIL_2_COLUMNS, CORE_DETAIL_3_COLUMNS,
    CORE_DETAIL_4_COLUMNS, CORE_DETAIL_5_COLUMNS,
    APPLICATION_1_COLUMNS, APPLICATION_2_COLUMNS,
} from './bulkColumnMappings';

const UploadScreen = ({ listingMode = 'existing', onShowFormHelper, onFetchFromCatalogue, loadingProducts, catalogueProducts = [], asinsToAdd, onAsinsAdded }) => {
    const authCtx = useContext(AuthContext);
    const { isBridgeActive } = useAmazonSync();
    const { connected: amazonConnected } = useAmazonStatus(authCtx.token);
    const [generationType, setGenerationType] = useState('complete_listing'); // Default: Complete Listing
    // Separate state for strict 1 Excel + 1 Zip constraint
    const [files, setFiles] = useState({
        excel: null,
        zip: null
    });
    const fileInputRef = useRef(null);
    const [errorMsg, setErrorMsg] = useState(null);
    const [parsedData, setParsedData] = useState(null);
    const [isParsing, setIsParsing] = useState(false);
    const [isFetchingAsins, setIsFetchingAsins] = useState(false);
    const [notFoundAsins, setNotFoundAsins] = useState([]);
    const [showNotFoundModal, setShowNotFoundModal] = useState(false);

    // ASIN state for existing mode
    const [asins, setAsins] = useState([]);
    const [asinInput, setAsinInput] = useState('');
    const asinInputRef = useRef(null);

    // Sync ASINs added from product catalogue modal
    useEffect(() => {
        if (asinsToAdd && asinsToAdd.length > 0) {
            setAsins(prev => {
                const newAsins = [...prev];
                asinsToAdd.forEach(asinToAdd => {
                    if (!newAsins.includes(asinToAdd)) {
                        newAsins.push(asinToAdd);
                    }
                });
                return newAsins;
            });
            if (onAsinsAdded) {
                onAsinsAdded();
            }
        }
    }, [asinsToAdd, onAsinsAdded]);

    const parseAndAddAsins = (text) => {
        const parsed = text
            .split(/[\n,]+/)
            .map(s => s.trim())
            .filter(Boolean);
        if (parsed.length === 0) return;
        setAsins(prev => {
            const newAsins = [...prev];
            parsed.forEach(a => {
                if (!newAsins.includes(a)) newAsins.push(a);
            });
            return newAsins;
        });
        setAsinInput('');
    };

    const handleAsinInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            parseAndAddAsins(asinInput);
        }
        // Backspace on empty input removes last tag
        if (e.key === 'Backspace' && asinInput === '' && asins.length > 0) {
            setAsins(prev => prev.slice(0, -1));
        }
    };

    const handleAsinPaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text');
        parseAndAddAsins(pasted);
    };

    const removeAsin = (index) => {
        setAsins(prev => prev.filter((_, i) => i !== index));
    };

    const validateAsinsAgainstCatalogue = (asinsToValidate) => {
        // Skip validation for open accounts (can use external ASINs).
        // Also skip while the user's Amazon catalogue is still syncing —
        // they get temporary Rainforest access during the bridge window.
        const accountType = authCtx?.user?.account_type || 'close';
        if (accountType === 'open' || isBridgeActive) {
            return { valid: asinsToValidate, notFound: [] };
        }

        let products = catalogueProducts;
        if (!products || products.length === 0) {
            try {
                const cached = JSON.parse(localStorage.getItem('bulk_catalogue_products'));
                if (cached?.products?.length > 0) products = cached.products;
            } catch (e) {
                console.warn('Failed to read cached catalogue:', e);
            }
        }
        if (!products || products.length === 0) {
            return { valid: [], notFound: asinsToValidate };
        }
        const catalogueAsins = new Set(products.map(p => p.asin));
        const valid = asinsToValidate.filter(a => catalogueAsins.has(a));
        const notFound = asinsToValidate.filter(a => !catalogueAsins.has(a));
        return { valid, notFound };
    };

    const buildRowFromEntry = (entry, parentData, isParent, parentSku) => {
        const p = parentData || {};
        const bullets = Array.isArray(entry.bullet_points) && entry.bullet_points.length > 0
            ? entry.bullet_points
            : (Array.isArray(p.bullet_points) ? p.bullet_points : []);
        return {
            SKU: entry.sku || entry.asin || '',
            'SKU ID': entry.asin || '',
            isParent,
            'Parent SKU': parentSku,
            Title: entry.title || p.title || '',
            'Product Description': entry.product_description || p.product_description || '',
            'Bullet Point 1': bullets[0] || '',
            'Bullet Point 2': bullets[1] || '',
            'Bullet Point 3': bullets[2] || '',
            'Bullet Point 4': bullets[3] || '',
            'Bullet Point 5': bullets[4] || '',
            Brand: entry.brand || p.brand || '',
            Category: '',
            'Sub Category': '',
            'Product Type': entry.product_type || p.product_type || '',
            Color: entry.color || p.color || '',
            Material: entry.material || p.material || '',
            'Size/Dimensions': entry.size || entry.dimensions || p.size || p.dimensions || '',
            EAN: '',
            'Additional Keywords (comma-separated)': '',
            'Banned Keywords': '',
            'Application 1 (Give a use case statement)': '',
            'Application 2 (Give a use case statement)': '',
            '_categories_flat': '',
        };
    };

    const getEntryImages = (entry, parentData) => {
        const entryImgs = Array.isArray(entry.images) && entry.images.length > 0 ? entry.images : [];
        const parentImgs = Array.isArray(parentData?.images) ? parentData.images : [];
        return entryImgs.length > 0 ? entryImgs : parentImgs;
    };

    const hydrateImages = async (imageEntries, maxPerSku = 7) => {
        const prefetchedSkuImages = {};
        for (const { sku, images } of imageEntries) {
            if (!sku) continue;
            const urls = images
                .map(u => (typeof u === 'object' && u !== null ? (u.link || '') : (typeof u === 'string' ? u : '')).trim())
                .filter(Boolean)
                .slice(0, maxPerSku);
            const hydrated = await Promise.all(
                urls.map(async (imageUrl, i) => {
                    try {
                        const resp = await fetch(imageUrl);
                        const blob = await resp.blob();
                        const extFromType = (blob.type || '').includes('png') ? 'png' : 'jpg';
                        const name = `catalogue_${i + 1}.${extFromType}`;
                        const file = new File([blob], name, { type: blob.type || 'image/jpeg' });
                        const previewUrl = await new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.readAsDataURL(blob);
                        });
                        return { file, previewUrl, name };
                    } catch (e) {
                        return null;
                    }
                })
            );
            prefetchedSkuImages[sku] = hydrated.filter(Boolean);
        }
        return prefetchedSkuImages;
    };

    const processCatalogueGroups = (groups, asinsToFetch) => {
        const selectedSet = new Set(asinsToFetch);
        const excelRows = [];
        const allImageEntries = [];
        const foundAsins = new Set();

        // Track all ASINs present in catalogue response
        for (const group of groups) {
            if (group.parent_asin) foundAsins.add(group.parent_asin);
            if (group.parent_data?.asin) foundAsins.add(group.parent_data.asin);
            for (const child of (group.children || [])) {
                if (child.asin) foundAsins.add(child.asin);
            }
        }

        for (const group of groups) {
            const parentData = group.parent_data || {};
            const children = group.children || [];
            const parentAsin = group.parent_asin || '';
            const parentSelected = selectedSet.has(parentAsin);

            if (children.length > 0) {
                const selectedChildren = children.filter(c => selectedSet.has(c.asin));
                const parentSkuValue = parentData.sku || parentData.asin || parentAsin;

                if (parentSelected && selectedChildren.length > 0) {
                    excelRows.push(buildRowFromEntry(parentData, {}, true, ''));
                    allImageEntries.push({ sku: parentSkuValue, images: getEntryImages(parentData, {}) });
                    foundAsins.add(parentAsin);
                    for (const child of selectedChildren) {
                        excelRows.push(buildRowFromEntry(child, parentData, false, parentSkuValue));
                        allImageEntries.push({ sku: child.sku || child.asin, images: getEntryImages(child, parentData) });
                        foundAsins.add(child.asin);
                    }
                } else if (selectedChildren.length > 1) {
                    excelRows.push(buildRowFromEntry(parentData, {}, true, ''));
                    allImageEntries.push({ sku: parentSkuValue, images: getEntryImages(parentData, {}) });
                    foundAsins.add(parentAsin);
                    for (const child of selectedChildren) {
                        excelRows.push(buildRowFromEntry(child, parentData, false, parentSkuValue));
                        allImageEntries.push({ sku: child.sku || child.asin, images: getEntryImages(child, parentData) });
                        foundAsins.add(child.asin);
                    }
                } else if (selectedChildren.length === 1) {
                    const child = selectedChildren[0];
                    excelRows.push(buildRowFromEntry(child, parentData, true, ''));
                    allImageEntries.push({ sku: child.sku || child.asin, images: getEntryImages(child, parentData) });
                    foundAsins.add(child.asin);
                } else if (parentSelected) {
                    const parentSkuValue = parentData.sku || parentData.asin || parentAsin;
                    excelRows.push(buildRowFromEntry(parentData, {}, true, ''));
                    allImageEntries.push({ sku: parentSkuValue, images: getEntryImages(parentData, {}) });
                    foundAsins.add(parentAsin);
                    // When parent is selected, also include ALL children
                    for (const child of children) {
                        excelRows.push(buildRowFromEntry(child, parentData, false, parentSkuValue));
                        allImageEntries.push({ sku: child.sku || child.asin, images: getEntryImages(child, parentData) });
                        foundAsins.add(child.asin);
                    }
                }
            } else {
                excelRows.push(buildRowFromEntry(parentData, {}, true, ''));
                allImageEntries.push({ sku: parentData.sku || parentData.asin, images: getEntryImages(parentData, {}) });
                foundAsins.add(parentData.asin || parentAsin);
            }
        }
        return { excelRows, allImageEntries, foundAsins };
    };

    const fetchViaRainforest = async (missingAsins, headers) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/rainforest/lookup-asins`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                asins: missingAsins,
                amazon_domain: 'amazon.in',
                listingMode: 'existing',
                max_asins: 100,
            }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) return { excelRows: [], imageEntries: [] };

        const results = Array.isArray(data?.results) ? data.results : [];
        const successes = results.filter(r => r?.success && r?.result);
        const excelRows = [];
        const imageEntries = [];

        for (const r of successes) {
            const asin = r.asin;
            const rf = r.result || {};
            const bullets = Array.isArray(rf.existing_bullets) ? rf.existing_bullets
                : (Array.isArray(rf.bullets) ? rf.bullets : []);
            excelRows.push({
                SKU: asin, 'SKU ID': asin, isParent: true, 'Parent SKU': '',
                Title: rf.existing_title || rf.title || '',
                'Product Description': rf.existing_description || rf.description || '',
                'Bullet Point 1': bullets[0] || '', 'Bullet Point 2': bullets[1] || '',
                'Bullet Point 3': bullets[2] || '', 'Bullet Point 4': bullets[3] || '',
                'Bullet Point 5': bullets[4] || '',
                Brand: rf.brand_name || rf.brandName || '',
                Category: rf.category || '', 'Sub Category': rf.sub_category || '',
                'Product Type': rf.product_type || rf.productType || '',
                Color: rf.color || '', Material: rf.material || '',
                'Size/Dimensions': rf.size || '', EAN: rf.ean_number || rf.eanNumber || '',
                'Additional Keywords (comma-separated)': '', 'Banned Keywords': '',
                'Application 1 (Give a use case statement)': '',
                'Application 2 (Give a use case statement)': '',
                '_categories_flat': rf.categories_flat || '',
            });
            const urls = Array.from(new Set(
                [rf.main_image_url, rf.main_image,
                    ...(Array.isArray(rf.all_images) ? rf.all_images : []),
                    ...(Array.isArray(rf.images) ? rf.images : [])]
                    .map(u => (typeof u === 'string' ? u.trim() : '')).filter(Boolean)
            ));
            imageEntries.push({ sku: asin, images: urls.map(link => link) });
        }
        return { excelRows, imageEntries };
    };

    const proceedWithFetch = async (asinsToFetch, { useRainforestFallback = false, skipCatalogue = false } = {}) => {
        setErrorMsg(null);
        setIsFetchingAsins(true);
        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const headers = {
                'Authorization': `Bearer ${authCtx.token}`,
                'Content-Type': 'application/json',
            };

            // Step 1: Try catalogue fetch (skip if all ASINs are outside catalogue)
            let catalogueRows = [], catalogueImageEntries = [], foundAsins = new Set();
            if (!skipCatalogue) {
                try {
                    const response = await fetch(`${API_URL}/amazon/catalogue/fetch?page_size=${asinsToFetch.length}`, {
                        method: 'POST', headers,
                        body: JSON.stringify({ asins: asinsToFetch }),
                    });
                    const data = await response.json().catch(() => ({}));
                    if (response.ok) {
                        const groups = Array.isArray(data?.data) ? data.data : [];
                        const result = processCatalogueGroups(groups, asinsToFetch);
                        catalogueRows = result.excelRows;
                        catalogueImageEntries = result.allImageEntries;
                        foundAsins = result.foundAsins;
                    }
                } catch (e) {
                    console.warn('Catalogue fetch failed:', e);
                    setErrorMsg('Could not fetch catalogue data. Falling back to other sources.');
                }
            }

            // Step 2: Fallback to rainforest for missing ASINs (only if allowed)
            const missingAsins = asinsToFetch.filter(a => !foundAsins.has(a));
            let rainforestRows = [], rainforestImageEntries = [];
            if (useRainforestFallback && missingAsins.length > 0) {
                const rfResult = await fetchViaRainforest(missingAsins, headers);
                rainforestRows = rfResult.excelRows;
                rainforestImageEntries = rfResult.imageEntries;
            }

            // Step 3: Merge results
            const allExcelRows = [...catalogueRows, ...rainforestRows];
            if (allExcelRows.length === 0) {
                setErrorMsg('No product data found for given ASINs.');
                return;
            }

            // Hydrate images from both sources
            const allImageEntries = [...catalogueImageEntries, ...rainforestImageEntries];
            const prefetchedSkuImages = await hydrateImages(allImageEntries);

            const nextParsedData = {
                allExcelRows,
                generationType,
                prefetchedSkuImages,
                files: [],
            };
            setParsedData(nextParsedData);
            onShowFormHelper(nextParsedData, null);
        } catch (e) {
            setErrorMsg(e?.message || 'Failed to fetch ASIN data. Please try again.');
        } finally {
            setIsFetchingAsins(false);
        }
    };

    const handleExcludeAndContinue = () => {
        const validAsins = asins.filter(a => !notFoundAsins.includes(a));
        setAsins(validAsins);
        setShowNotFoundModal(false);
        setNotFoundAsins([]);
        if (validAsins.length > 0) {
            proceedWithFetch(validAsins);
        }
    };

    const handleContinueWithAll = () => {
        setShowNotFoundModal(false);
        setNotFoundAsins([]);
        proceedWithFetch(asins, { useRainforestFallback: true });
    };

    const handleCloseNotFoundModal = () => {
        setShowNotFoundModal(false);
        setNotFoundAsins([]);
    };

    const handleFetchAsins = async () => {
        if (!asins || asins.length === 0) return;
        if (!authCtx?.token) {
            setErrorMsg('Please log in to fetch ASIN data.');
            return;
        }

        setErrorMsg(null);

        // Open accounts (and closed accounts in bridge mode) skip catalogue
        // validation and use Rainforest directly.
        const accountType = authCtx?.user?.account_type || 'close';
        if (accountType === 'open' || isBridgeActive) {
            proceedWithFetch(asins, { useRainforestFallback: true });
            return;
        }

        // Fetch catalogue products for validation
        let products = catalogueProducts;
        if (!products || products.length === 0) {
            try {
                const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                const res = await fetch(`${API_URL}/amazon/catalogue`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${authCtx.token}`,
                        'Content-Type': 'application/json',
                    },
                });
                if (res.ok) {
                    const data = await res.json();
                    products = data.products || [];
                }
            } catch (e) {
                console.warn('Catalogue validation fetch failed:', e);
            }
        }

        const catalogueAsins = new Set((products || []).map(p => p.asin));
        const valid = asins.filter(a => catalogueAsins.has(a));
        const notFound = asins.filter(a => !catalogueAsins.has(a));

        if (notFound.length > 0) {
            setNotFoundAsins(notFound);
            setShowNotFoundModal(true);
            return;
        }
        proceedWithFetch(valid);
    };

    const handleDownloadTemplate = () => {
        const headers = [
            'SKU', 'Brand', 'Category', 'Sub Category', 'Product Type', 'Title',
            'Color', 'Material', 'Size', 'EAN', 'Keywords', 'Description',
            'Bullet Point 1', 'Bullet Point 2', 'Bullet Point 3', 'Bullet Point 4', 'Bullet Point 5',
            'is_parent', 'Parent SKU',
        ];
        const ws = XLSX.utils.aoa_to_sheet([headers]);
        ws['!cols'] = headers.map(() => ({ wch: 20 }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Bulk Template');
        XLSX.writeFile(wb, 'bulk_existing_listing_template.xlsx');
    };

    const handleUploadFiles = () => {
        // Trigger the hidden file input
        fileInputRef.current.click();
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const validateFile = (file) => {
        const validExcelExts = ['xls', 'xlsx', 'xlsm', 'csv'];
        const validZipExts = ['zip'];
        const fileExtension = file.name.split('.').pop().toLowerCase();

        // Check extension
        const isExcel = validExcelExts.includes(fileExtension);
        const isZip = validZipExts.includes(fileExtension);

        if (!isExcel && !isZip) {
            return { valid: false, msg: `Invalid file type: .${fileExtension}. Please upload Excel (csv/xls/xlsx/xlsm) or ZIP.` };
        }

        // Max size 50MB
        const maxSize = 50 * 1024 * 1024;
        if (file.size > maxSize) {
            return { valid: false, msg: `File too large: ${file.name}. Max size is 50MB.` };
        }

        return { valid: true, type: isExcel ? 'excel' : 'zip' };
    };

    // Parse Excel file
    const parseExcelFile = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];

                    // Get raw data as array of arrays
                    const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

                    // Check if this Excel has a special structure (row 0 = category headers, row 1 = column names)
                    // Detect by checking if row 0 has many empty cells and row 1 looks like headers
                    let headerRowIndex = 0;
                    if (rawData.length >= 2) {
                        const row0EmptyCount = rawData[0].filter(cell => cell === '' || cell === null || cell === undefined).length;
                        const row0TotalCells = rawData[0].length;

                        // If row 0 has more than 50% empty cells, assume row 1 has the actual headers
                        if (row0EmptyCount > row0TotalCells * 0.5) {
                            headerRowIndex = 1;
                        }
                    }

                    // Use detected header row
                    const headers = rawData[headerRowIndex];
                    const dataStartRow = headerRowIndex + 1;

                    // Convert to array of objects using detected headers
                    const jsonData = [];
                    for (let i = dataStartRow; i < rawData.length; i++) {
                        const row = rawData[i];
                        if (!row || row.every(cell => cell === '' || cell === null || cell === undefined)) {
                            continue; // Skip empty rows
                        }
                        const obj = {};
                        headers.forEach((header, index) => {
                            if (header && header.toString().trim()) {
                                obj[header.toString().trim()] = row[index] !== undefined ? row[index] : '';
                            }
                        });
                        jsonData.push(obj);
                    }

                    resolve(jsonData);
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });
    };

    // Parse ZIP file to extract folder structure (SKU codes)
    const parseZipFile = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const zip = new JSZip();
                    const zipContent = await zip.loadAsync(e.target.result);
                    const folders = new Set();
                    
                    // Extract folder names (SKU codes) from ZIP structure
                    Object.keys(zipContent.files).forEach((filename) => {
                        // Skip hidden files and system files
                        if (filename.startsWith('__MACOSX') || filename.startsWith('.DS_Store') || filename.startsWith('._')) {
                            return;
                        }
                        
                        // Check if it's a folder (ends with /) or a file in a folder
                        const parts = filename.split('/').filter(p => p);
                        if (parts.length > 0) {
                            // Get the first part as folder name (SKU code)
                            const folderName = parts[0];
                            if (folderName && folderName.trim()) {
                                folders.add(folderName.trim());
                            }
                        }
                    });
                    
                    resolve(Array.from(folders).sort());
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });
    };

    // Auto-fill form data from parsed Excel (existing mode: includes title/bullets/description)
    const extractFormDataFromExcelExisting = (excelData) => {
        if (!excelData || excelData.length === 0) return null;
        const firstRow = excelData[0];
        const formData = {
            brandName: findColumnValue(firstRow, BRAND_COLUMNS),
            category: findColumnValue(firstRow, CATEGORY_COLUMNS),
            subCategory: findColumnValue(firstRow, SUB_CATEGORY_COLUMNS),
            productType: findColumnValue(firstRow, PRODUCT_TYPE_COLUMNS),
            modelNumber: findColumnValue(firstRow, SKU_COLUMNS),
            color: findColumnValue(firstRow, COLOR_COLUMNS),
            material: findColumnValue(firstRow, MATERIAL_COLUMNS),
            size: findColumnValue(firstRow, SIZE_COLUMNS),
            eanNumber: findColumnValue(firstRow, EAN_COLUMNS),
            keywords: findColumnValue(firstRow, KEYWORDS_COLUMNS),
            briefDescription: findColumnValue(firstRow, DESCRIPTION_COLUMNS),
            feature1: findColumnValue(firstRow, BULLET_1_COLUMNS),
            feature2: findColumnValue(firstRow, BULLET_2_COLUMNS),
            feature3: findColumnValue(firstRow, BULLET_3_COLUMNS),
            feature4: findColumnValue(firstRow, BULLET_4_COLUMNS),
            feature5: findColumnValue(firstRow, BULLET_5_COLUMNS),
        };
        const existingInput = {
            title: findColumnValue(firstRow, TITLE_COLUMNS),
            description: findColumnValue(firstRow, DESCRIPTION_COLUMNS),
            bullets: [
                findColumnValue(firstRow, BULLET_1_COLUMNS),
                findColumnValue(firstRow, BULLET_2_COLUMNS),
                findColumnValue(firstRow, BULLET_3_COLUMNS),
                findColumnValue(firstRow, BULLET_4_COLUMNS),
                findColumnValue(firstRow, BULLET_5_COLUMNS),
            ]
        };
        return { formData, existingInput, allRows: excelData };
    };

    // New mode: same structure but Excel may omit Title/bullets; we still pass all rows for per-SKU form data
    const extractFormDataFromExcelNew = (excelData) => {
        if (!excelData || excelData.length === 0) return null;
        const firstRow = excelData[0];
        const formData = {
            brandName: findColumnValue(firstRow, BRAND_COLUMNS),
            category: findColumnValue(firstRow, CATEGORY_COLUMNS),
            subCategory: findColumnValue(firstRow, SUB_CATEGORY_COLUMNS),
            productType: findColumnValue(firstRow, PRODUCT_TYPE_COLUMNS),
            modelNumber: findColumnValue(firstRow, SKU_COLUMNS),
            color: findColumnValue(firstRow, COLOR_COLUMNS),
            material: findColumnValue(firstRow, MATERIAL_COLUMNS),
            size: findColumnValue(firstRow, SIZE_COLUMNS),
            eanNumber: findColumnValue(firstRow, EAN_COLUMNS),
            keywords: findColumnValue(firstRow, KEYWORDS_COLUMNS),
            bannedKeywords: findColumnValue(firstRow, BANNED_KEYWORDS_COLUMNS),
            briefDescription: findColumnValue(firstRow, DESCRIPTION_COLUMNS),
            feature1: findColumnValue(firstRow, CORE_DETAIL_1_COLUMNS),
            feature2: findColumnValue(firstRow, CORE_DETAIL_2_COLUMNS),
            feature3: findColumnValue(firstRow, CORE_DETAIL_3_COLUMNS),
            feature4: findColumnValue(firstRow, CORE_DETAIL_4_COLUMNS),
            feature5: findColumnValue(firstRow, CORE_DETAIL_5_COLUMNS),
            application1: findColumnValue(firstRow, APPLICATION_1_COLUMNS),
            application2: findColumnValue(firstRow, APPLICATION_2_COLUMNS),
        };
        const existingInput = { title: '', description: '', bullets: ['', '', '', '', ''] };
        return { formData, existingInput, allRows: excelData };
    };

    const extractFormDataFromExcel = listingMode === 'new' ? extractFormDataFromExcelNew : extractFormDataFromExcelExisting;

    const handleFileChange = async (event) => {
        const selectedFiles = Array.from(event.target.files);
        if (selectedFiles.length === 0) return;

        setErrorMsg(null); // Clear previous errors

        let newFiles = { ...files };
        let msg = null;

        for (const file of selectedFiles) {
            const validation = validateFile(file);
            if (!validation.valid) {
                msg = validation.msg;
                continue;
            }

            if (validation.type === 'excel') {
                if (newFiles.excel) {
                    msg = "An Excel file is already uploaded. Please remove it first to upload a new one.";
                } else {
                    newFiles.excel = {
                        name: file.name,
                        size: formatFileSize(file.size),
                        fileObj: file
                    };
                }
            } else if (validation.type === 'zip') {
                if (newFiles.zip) {
                    msg = "A ZIP file is already uploaded. Please remove it first to upload a new one.";
                } else {
                    newFiles.zip = {
                        name: file.name,
                        size: formatFileSize(file.size),
                        fileObj: file
                    };
                }
            }
        }

        if (msg) {
            setErrorMsg(msg);
        }
        setFiles(newFiles);

        // Auto-parse files when both are uploaded
        if (newFiles.excel && newFiles.zip) {
            setIsParsing(true);
            try {
                const excelData = await parseExcelFile(newFiles.excel.fileObj);
                const zipFolders = await parseZipFile(newFiles.zip.fileObj);
                const extractedData = extractFormDataFromExcel(excelData);
                
                setParsedData({
                    excelData: extractedData,
                    zipFolders: zipFolders,
                    allExcelRows: excelData
                });
            } catch (error) {
                console.error('Error parsing files:', error);
                setErrorMsg('Error parsing files. Please check the file format.');
            } finally {
                setIsParsing(false);
            }
        } else if (newFiles.excel) {
            // Parse only Excel if available
            setIsParsing(true);
            try {
                const excelData = await parseExcelFile(newFiles.excel.fileObj);
                const extractedData = extractFormDataFromExcel(excelData);
                
                setParsedData({
                    excelData: extractedData,
                    zipFolders: [],
                    allExcelRows: excelData
                });
            } catch (error) {
                console.error('Error parsing Excel file:', error);
                setErrorMsg('Error parsing Excel file. Please check the file format.');
            } finally {
                setIsParsing(false);
            }
        }

        // Reset input value to allow re-uploading the same file if needed (after removal)
        event.target.value = '';
    };

    const handleAddMore = () => {
        if (files.excel && files.zip) {
            return; // Should be disabled anyway
        }
        fileInputRef.current.click();
    };

    const handleRemoveFile = (type) => {
        setFiles(prev => ({ ...prev, [type]: null }));
        setErrorMsg(null);
        // Clear parsed data if removing files
        if (type === 'excel' || type === 'zip') {
            setParsedData(null);
        }
    };

    const handleGenerate = () => {
        if (!files.excel) {
            setErrorMsg("Please upload an Excel file.");
            return;
        }
        // ZIP is technically optional in some flows, but usually required for bulk with images.
        // User requirement table says "Zip 1 only", doesn't explicitly say mandatory for generation but implies "required files uploaded".
        // And "Attempting to upload after both slots are filled".
        // It implies we expect both. But let's check basic requirement: "Excel Required for generation", "ZIP Optional (but recommended)".
        // Wait, standard user requirement earlier said "ZIP Optional".
        // But the new strict rule says "Lock Upload After Both Files Are Present".
        // I will allow generate if at least Excel is present, but validation logic is strict on counts.

        const filesToUpload = [];
        if (files.excel) filesToUpload.push(files.excel.fileObj);
        if (files.zip) filesToUpload.push(files.zip.fileObj);

        // Switch to FormHelper and pass parsed data (with generationType) and zip file
        onShowFormHelper({ ...parsedData, generationType }, files.zip?.fileObj || null);
    };

    const isFull = files.excel && files.zip;
    const hasFiles = files.excel || files.zip;

    return (
        <>
        <div className={styles.container}>
            {/* Hidden File Input */}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                multiple
                accept=".csv, .xls, .xlsx, .xlsm, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.ms-excel.sheet.macroEnabled.12, .zip, application/zip, application/x-zip-compressed"
                onChange={handleFileChange}
                disabled={isFull}
            />

            {/* Page Header */}
            <div className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>Bulk Listing Generator</h1>
                <div className={styles.titleDivider}></div>
                <div className={styles.radioGroupHeader}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g opacity="0.7">
                            <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="#6794C5" />
                            <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="#6794C5" />
                        </g>
                    </svg>
                    What do you want to generate?
                </div>

                <div className={styles.radioGroup}>
                    <label className={styles.radioLabel}>
                        <input
                            type="radio"
                            name="generationType"
                            value="product_copy"
                            checked={generationType === 'product_copy'}
                            onChange={() => setGenerationType('product_copy')}
                            className={styles.radioInput}
                        />
                        Product Copy
                    </label>
                    <label className={styles.radioLabel}>
                        <input
                            type="radio"
                            name="generationType"
                            value="complete_listing"
                            checked={generationType === 'complete_listing'}
                            onChange={() => setGenerationType('complete_listing')}
                            className={styles.radioInput}
                        />
                        Complete Listing
                    </label>
                </div>
            </div>

            {/* Error Message Toast/Banner */}
            {errorMsg && (
                <div style={{
                    color: '#d32f2f',
                    backgroundColor: '#ffebee',
                    padding: '10px',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    textAlign: 'center',
                    maxWidth: '600px',
                    margin: '0 auto 20px auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                }}>
                    <FaExclamationCircle /> {errorMsg}
                </div>
            )}

            {/* ASIN Search Input - Existing mode only */}
            {listingMode === 'existing' && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                    <div className={searchStyles.newSearchInputContainer}>
                        <div className={searchStyles.newSearchInputCard}>
                            <div
                                className={searchStyles.newSearchInputWrapper}
                                onClick={() => asinInputRef.current?.focus()}
                            >
                                {asins.map((asinValue, index) => (
                                    <div key={index} className={searchStyles.asinTag} title={asinValue}>
                                        <span className={searchStyles.asinTagText}>
                                            {asinValue.length > 10 ? asinValue.slice(0, 10) + '…' : asinValue}
                                        </span>
                                        <button
                                            type="button"
                                            className={searchStyles.asinTagRemove}
                                            onClick={() => removeAsin(index)}
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                ))}
                                <input
                                    ref={asinInputRef}
                                    type="text"
                                    className={searchStyles.newSearchInput}
                                    placeholder={asins.length === 0 ? 'Type or paste ASINs (comma or line-break separated)' : 'Add more…'}
                                    value={asinInput}
                                    onChange={(e) => setAsinInput(e.target.value)}
                                    onKeyDown={handleAsinInputKeyDown}
                                    onPaste={handleAsinPaste}
                                    onBlur={() => { if (asinInput.trim()) parseAndAddAsins(asinInput); }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={styles.buttonGroup} style={{ margin: '16px 0' }}>
                        <button
                            type="button"
                            className={styles.primaryButton}
                            onClick={handleFetchAsins}
                            disabled={isFetchingAsins || asins.length === 0}
                        >
                            {isFetchingAsins ? 'Fetching…' : 'Fetch'}
                        </button>
                    </div>
                    {onFetchFromCatalogue && (
                        <div className={searchStyles.fetchFromCatalogueWrapper}>
                            <button
                                type="button"
                                onClick={onFetchFromCatalogue}
                                className={`${searchStyles.fetchFromCatalogueLink} ${!amazonConnected ? searchStyles.fetchFromCatalogueLinkDisabled : ''}`}
                                disabled={loadingProducts || !amazonConnected}
                                data-tooltip={!amazonConnected ? "Connect your Amazon account to use" : ""}
                            >
                                Fetch from Product Catalogue
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Main Upload Card - Hidden for existing mode */}
            {listingMode !== 'existing' && <div className={styles.uploadCard}>
                <p className={styles.uploadText}>
                    Upload your product data (CSV/Excel) following the template structure. Our AI will then generate compelling titles, descriptions, bullet points, and keywords for your listings.
                </p>
                <div className={styles.buttonGroup}>
                    <button className={styles.textButton} onClick={handleDownloadTemplate}>
                        <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 17.7402H15.7695V19.7112H0V17.7402Z" fill="url(#paint0_linear_673_1565)" />
                            <path d="M15.4827 8.58444L14.0834 7.18507L8.87016 12.4083V0H6.89822V12.4083L1.68501 7.18507L0.285645 8.58444L7.88405 16.1728L15.4827 8.58444Z" fill="url(#paint1_linear_673_1565)" />
                            <defs>
                                <linearGradient id="paint0_linear_673_1565" x1="-5.87461e-08" y1="18.7257" x2="0.938622" y2="22.8167" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#2766AA" />
                                    <stop offset="1" stopColor="#0752B0" />
                                </linearGradient>
                                <linearGradient id="paint1_linear_673_1565" x1="0.285644" y1="8.08642" x2="14.6182" y2="15.423" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#2766AA" />
                                    <stop offset="1" stopColor="#0752B0" />
                                </linearGradient>
                            </defs>
                        </svg> Download Template
                    </button>

                    {/* Main Upload Button: Hide if ANY file is uploaded, OR disable if full?
                        User Req: "Disable the upload button entirely... Visually indicate: Required files uploaded"
                        And "Show Uploaded Files... below the upload area"
                        Previous design hid the big button once files started appearing.
                        Let's stick to hiding the big button and relying on the "Add More" button in the list,
                        or showing a disabled state?
                        The prompt says "Input File / Upload File(s) control... Lock Upload After Both... Disable the upload button entirely".
                        If I hide the big button, I can't disable it.
                        I will hide the Main Big Button once files are present (to show the list),
                        and controls the "Add More" button in the list.
                    */}
                    {!hasFiles && (
                        <button className={styles.primaryButton} onClick={handleUploadFiles}>
                            <FaPlus /> Upload File(s)
                        </button>
                    )}
                </div>
            </div>}

            {/* Uploaded Files Section - Visible when files exist */}
            {hasFiles && (
                <div className={styles.uploadedSection}>
                    <div className={styles.uploadedHeader}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.sparkleIcon}>
                            <g opacity="0.7">
                                <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="#6794C5" />
                                <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="#6794C5" />
                            </g>
                        </svg>
                        Uploaded Files
                    </div>

                    <div className={styles.filesContainer}>
                        {/* Excel Slot */}
                        {files.excel && (
                            <div className={styles.fileChip}>
                                <img src={excelIcon} alt="XLS" className={styles.fileIcon} />
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', overflow: 'hidden', flexGrow: 1 }}>
                                    <span className={styles.fileName} title={files.excel.name}>{files.excel.name}</span>
                                    <span style={{ fontSize: '0.75rem', color: '#888' }}>{files.excel.size}</span>
                                </div>
                                <button className={styles.removeFileBtn} onClick={() => handleRemoveFile('excel')}>
                                    <FaTimes />
                                </button>
                            </div>
                        )}

                        {/* Zip Slot */}
                        {files.zip && (
                            <div className={styles.fileChip}>
                                <img src={zipIcon} alt="ZIP" className={styles.fileIcon} />
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', overflow: 'hidden', flexGrow: 1 }}>
                                    <span className={styles.fileName} title={files.zip.name}>{files.zip.name}</span>
                                    <span style={{ fontSize: '0.75rem', color: '#888' }}>{files.zip.size}</span>
                                </div>
                                <button className={styles.removeFileBtn} onClick={() => handleRemoveFile('zip')}>
                                    <FaTimes />
                                </button>
                            </div>
                        )}

                        {/* Add More Button - Disabled/Hidden if full */}
                        {!isFull && (
                            <button className={styles.addMoreBtn} onClick={handleAddMore} title="Add more files">
                                <FaPlus />
                            </button>
                        )}
                    </div>

                    {isParsing && (
                        <div style={{
                            padding: '10px',
                            textAlign: 'center',
                            color: '#6794C5',
                            fontSize: '0.9rem',
                            marginTop: '10px'
                        }}>
                            Parsing files and extracting data...
                        </div>
                    )}

                    {parsedData && !isParsing && (
                        <div style={{
                            padding: '10px',
                            textAlign: 'center',
                            color: '#4caf50',
                            fontSize: '0.9rem',
                            marginTop: '10px',
                            backgroundColor: '#e8f5e9',
                            borderRadius: '4px'
                        }}>
                            ✓ Files parsed successfully. Form will be auto-filled.
                        </div>
                    )}

                    <p className={styles.uploadNote}>
                        Note: Create Folders in the file that exactly matches the SKU codes in the excel sheet uploaded
                    </p>

                    {(() => {
                        const parentCount = Array.isArray(parsedData?.allExcelRows)
                            ? (parsedData.allExcelRows.filter(r => r?.isParent !== false).length || asins.length)
                            : asins.length;
                        const totalCredits = parentCount * 50;
                        return (
                            <button
                                className={styles.generateButton}
                                onClick={handleGenerate}
                                disabled={isParsing}
                            >
                                <BsStars /> Generate Listing
                                {parentCount > 0 ? ` (${totalCredits} credits)` : ' (50 credits per parent)'}
                            </button>
                        );
                    })()}
                </div>
            )}

            {/* How it works Section */}
            <div className={styles.howItWorksSection}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionTitle}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g opacity="0.7">
                                <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="url(#paint0_linear_673_1577)" />
                                <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="#6794C5" />
                            </g>
                            <defs>
                                <linearGradient id="paint0_linear_673_1577" x1="-4.47035e-08" y1="10" x2="11.0143" y2="16" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#2766AA" />
                                    <stop offset="1" stopColor="#0752B0" />
                                </linearGradient>
                                <linearGradient id="paint1_linear_673_1577" x1="9" y1="3.5" x2="15.425" y2="7" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#2766AA" />
                                    <stop offset="1" stopColor="#0752B0" />
                                </linearGradient>
                            </defs>
                        </svg> How it works?
                    </div>
                    <div className={styles.watchTutorial}>Watch Tutorial</div>
                </div>

                <div className={styles.stepsContainer}>
                    {listingMode === 'existing' ? (
                        <>
                            <div className={styles.stepCard}>
                                <div className={styles.stepNumber}>Step 1</div>
                                <div className={styles.stepDescription}>
                                    Enter ASINs manually or fetch products from your Amazon catalogue. ASINs must exist in your seller catalogue.
                                </div>
                            </div>
                            <div className={styles.stepCard}>
                                <div className={styles.stepNumber}>Step 2</div>
                                <div className={styles.stepDescription}>
                                    Choose generation type — Complete Listing (images + content) or Product Copy (content only).
                                </div>
                            </div>
                            <div className={styles.stepCard}>
                                <div className={styles.stepNumber}>Step 3</div>
                                <div className={styles.stepDescription}>
                                    Review and edit product details for each SKU.
                                </div>
                            </div>
                            <div className={styles.stepCard}>
                                <div className={styles.stepNumber}>Step 4</div>
                                <div className={styles.stepDescription}>
                                    Select themes and generate your listings.
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={styles.stepCard}>
                                <div className={styles.stepNumber}>Step 1</div>
                                <div className={styles.stepDescription}>
                                    Download the template, fill in product details, and ensure model_number matches your image folder names.
                                </div>
                                <img src={step1Image} alt="Step 1 Excel Preview" className={styles.stepImage} />
                            </div>
                            <div className={styles.stepCard}>
                                <div className={styles.stepNumber}>Step 2</div>
                                <div className={styles.stepDescription}>
                                    Create a folder with subfolders named exactly as the SKU codes. Add product images inside each folder and compress it into a .zip file.
                                </div>
                            </div>
                            <div className={styles.stepCard}>
                                <div className={styles.stepNumber}>Step 3</div>
                                <div className={styles.stepDescription}>
                                    Upload the Excel sheet and .zip file, then search and select the product type for each SKU.
                                </div>
                                <img src={step4Image} alt="Step 3 Upload Preview" className={styles.stepImage} />
                            </div>
                            <div className={styles.stepCard}>
                                <div className={styles.stepNumber}>Step 4</div>
                                <div className={styles.stepDescription}>
                                    Review product details, select themes, and generate your listings.
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>

        <AsinNotFoundModal
            isOpen={showNotFoundModal}
            notFoundAsins={notFoundAsins}
            onExcludeAndContinue={handleExcludeAndContinue}
            onContinueWithAll={handleContinueWithAll}
            onClose={handleCloseNotFoundModal}
        />
        </>
    );
};

export default UploadScreen;